/**
 * Display data (image, brand, price) of a catalogue product, looked up by
 * article number + supplier through the same query the product details use.
 * Needed when the signed image URL of a product has expired (and, for the
 * product selection, when a product shows up without display data).
 */
import type { ApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { isImage, processAttachment } from "@msys/ui";
import {
  PimGetProductByArticleNumberIdentifierDocument,
  type PimGetProductByArticleNumberIdentifierQuery,
  type PimGetProductByArticleNumberIdentifierQueryVariables,
} from "../../products/Products.generated.js";

export type ProductIdentity = { articleNumber: string; supplierId: string };

/** A product as an assistant view shows it; the display fields are optional. */
export type ProductPick = ProductIdentity & {
  title: string;
  brand?: string | null;
  imageUrl?: string | null;
  price?: { amount: number; currency: string } | null;
};

export type ProductDisplay = Pick<ProductPick, "imageUrl" | "brand" | "price">;

export function productId(product: ProductIdentity): string {
  return `${product.supplierId}:${product.articleNumber}`;
}

/** Always asks the network: a cached product would carry the expired URL again. */
export async function fetchProductDisplay(
  client: ApolloClient<object>,
  product: ProductIdentity
): Promise<ProductDisplay | null> {
  try {
    const result = await client.query<
      PimGetProductByArticleNumberIdentifierQuery,
      PimGetProductByArticleNumberIdentifierQueryVariables
    >({
      query: PimGetProductByArticleNumberIdentifierDocument,
      variables: {
        articleNumber: product.articleNumber,
        supplierId: product.supplierId,
      },
      fetchPolicy: "network-only",
    });
    const found = getDataOrNull(result.data?.pimGetProduct)?.product;
    if (!found) return null;
    const image = found.attachments
      .map(attachment =>
        processAttachment({ ...attachment, id: attachment.id.toString() })
      )
      .find(isImage);
    const amount = found.pricing?.listPrice ?? found.pricing?.netPrice;
    return {
      imageUrl: image?.url ?? null,
      brand: found.branding?.brand.title ?? null,
      price:
        amount === undefined || amount === null
          ? null
          : { amount, currency: "EUR" },
    };
  } catch {
    return null;
  }
}
